import { apiClient } from './api'
import { User, UserPermissionsResponse } from '../types'

/**
 * Gets all users
 * @returns Promise with array of user objects
 */
export function getUsers() {
  return apiClient.get<User[]>('/users')
}

/**
 * Gets user by ID
 * @param id - User ID
 * @returns Promise with user object
 */
export function getUserById(id: string) {
  return apiClient.get<User>(`/users/${id}`)
}

/**
 * Creates a new user
 * @param userData - User data object
 * @returns Promise with created user object
 */
export function createUser(userData: Partial<User> & { password: string }) {
  return apiClient.post<User>('/users', userData)
}

/**
 * Updates a user
 * @param id - User ID
 * @param userData - User data object
 * @returns Promise with updated user object
 */
export function updateUser(id: string, userData: Partial<User>) {
  return apiClient.put<User>(`/users/${id}`, userData)
}

/**
 * Activates or deactivates a user
 * @param id - User ID
 * @param isActive - Active status
 * @returns Promise with updated user object
 */
export function activateUser(id: string, isActive: boolean) {
  return apiClient.put<User>(`/users/${id}/activate`, { isActive })
}

/**
 * Deletes a user
 * @param id - User ID
 * @returns Promise void
 */
export function deleteUser(id: string) {
  return apiClient.delete(`/users/${id}`)
}

/**
 * Gets user permissions (resolved from role + custom)
 * @param id - User ID
 * @returns Promise with user permissions data
 */
export function getUserPermissions(id: string) {
  return apiClient.get<UserPermissionsResponse>(`/users/${id}/permissions`)
}

/**
 * Updates user permissions
 * @param id - User ID
 * @param data - Permission update data
 * @returns Promise with updated permissions
 */
export function updateUserPermissions(id: string, data: { customPermissions: boolean; permissions?: string[] }) {
  return apiClient.put(`/users/${id}/permissions`, data)
}
